import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Avatar,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded'
import API from '../../../services/API'
import { GlobalContext, UserContext } from '../../../App'

const fullName = (person) =>
  `${person?.firstName ?? ''} ${person?.lastName ?? ''}`.trim()

const initials = (person) =>
  `${person?.firstName?.[0] ?? ''}${person?.lastName?.[0] ?? ''}`.toUpperCase()

// The client's account manager: shown in the hero, changed in a small
// dialog. Whoever holds it gets a notification-bell alert for new notes,
// events and PTS bookings on this client (server: clientActivityAlerts.js).
const AccountManager = ({ client, onChange }) => {
  const { setToast } = useContext(GlobalContext)
  const { user } = useContext(UserContext)
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState([])
  const [loadingOptions, setLoadingOptions] = useState(false)
  const [selected, setSelected] = useState(null)
  const [saving, setSaving] = useState(false)

  const manager = client?.accountManager?._id ? client.accountManager : null
  const activityLabel = client?.orgType === 'PTS' ? 'PTS booking' : 'event'

  useEffect(() => {
    if (!open) return
    setSelected(manager)
    setLoadingOptions(true)
    API.get('/event-planning/organisations/account-manager-options')
      .then((res) => {
        if (res.status === 200) setOptions(res.data ?? [])
      })
      .catch(() => {})
      .finally(() => setLoadingOptions(false))
  }, [open])

  const me = useMemo(
    () => options.find((option) => option._id === user?._id) ?? null,
    [options, user?._id],
  )

  const save = (next) => {
    setSaving(true)
    API.put(`/event-planning/organisations/${client._id}/account-manager`, {
      accountManager: next?._id ?? null,
    })
      .then((res) => {
        if (res.status === 200) {
          onChange(res.data?.accountManager ?? null)
          setOpen(false)
          setToast({
            message: next
              ? `${fullName(next)} is now the account manager`
              : 'Account manager removed',
          })
        } else {
          setToast({
            message: res.data?.error || 'Unable to update the account manager.',
            severity: 'error',
          })
        }
      })
      .catch(() =>
        setToast({
          message: 'Unable to update the account manager.',
          severity: 'error',
        }),
      )
      .finally(() => setSaving(false))
  }

  const unchanged = (selected?._id ?? null) === (manager?._id ?? null)

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center" mt={1}>
        <Typography variant="caption" color="text.secondary">
          Account manager
        </Typography>
        {manager ? (
          <Chip
            size="small"
            avatar={<Avatar>{initials(manager)}</Avatar>}
            label={manager._id === user?._id ? `${fullName(manager)} (you)` : fullName(manager)}
            onClick={() => setOpen(true)}
            variant="outlined"
            sx={{ borderRadius: 1, fontWeight: 600 }}
          />
        ) : (
          <Chip
            size="small"
            icon={<PersonAddAlt1RoundedIcon />}
            label="Assign"
            onClick={() => setOpen(true)}
            variant="outlined"
            color="primary"
            sx={{ borderRadius: 1 }}
          />
        )}
      </Stack>

      <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Account manager</DialogTitle>
        <DialogContent>
          <Stack spacing={2} pt={1}>
            <Typography variant="body2" color="text.secondary">
              The account manager gets an alert in their notification bell whenever{' '}
              {client?.orgName || 'this client'} has a new note or a new {activityLabel},
              as a prompt to check in.
            </Typography>
            <Autocomplete
              options={options}
              loading={loadingOptions}
              value={selected}
              onChange={(_e, value) => setSelected(value)}
              getOptionLabel={(option) => fullName(option)}
              isOptionEqualToValue={(option, value) => option._id === value?._id}
              autoSelect
              autoHighlight
              renderInput={(params) => (
                <TextField {...params} label="Staff member" size="small" />
              )}
            />
            {!loadingOptions && options.length === 0 && (
              <Alert severity="info">
                Account managers come from the Event Planning team. Add someone to it in
                Site Configuration → Employees → Manage Access.
              </Alert>
            )}
            {me && selected?._id !== me._id && (
              <Button
                size="small"
                onClick={() => setSelected(me)}
                sx={{ alignSelf: 'flex-start' }}
              >
                Assign to me
              </Button>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          {manager && (
            <Button color="error" disabled={saving} onClick={() => save(null)} sx={{ mr: 'auto' }}>
              Remove
            </Button>
          )}
          <Button disabled={saving} onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saving || unchanged || !selected}
            onClick={() => save(selected)}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default AccountManager
